import { useState, useEffect, useRef } from 'react';
import { Sparkles, Target, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { reportError } from '@/lib/error-reporting';
import { getETypeColor, getPriorityColor } from '@/lib/goal-colors';
import { GoalSuggestionsResults } from '@/components/coach/results/GoalSuggestionsResults';
import { CreateGoalDialog } from '@/components/CreateGoalDialog';
import type { GoalSuggestions } from '@/components/coach/results/types';
import type { Goal, EType } from '@/types/goal';

interface GoalSuggestionsOnboardingProps {
  onAddGoal: (title: string, description: string, eTypes: EType[], targetDate?: Date) => Promise<Goal | null>;
  onDeleteGoal?: (id: string) => Promise<void>;
  onTabChange?: (tab: string) => void;
  onDone?: () => void;
  existingGoalCount?: number;
}

export const GoalSuggestionsOnboarding = ({
  onAddGoal,
  onDeleteGoal,
  onTabChange,
  onDone,
  existingGoalCount,
}: GoalSuggestionsOnboardingProps) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();

  const [suggestions, setSuggestions] = useState<GoalSuggestions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addedGoalMap, setAddedGoalMap] = useState<Map<string, string>>(new Map());
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!user || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchSuggestions();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      // Fetch user's core values for context
      const { data: valuesData } = await supabase
        .from('core_values')
        .select('value_name, description')
        .eq('user_id', user!.id)
        .order('importance_rank', { ascending: true });

      const valuesContext = valuesData?.map(v => v.value_name).join(', ') || '';

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/career-coach`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            type: 'goal_suggestions',
            job_role: profile?.job_role || null,
            target_job_role: profile?.target_job_role || null,
            skills: profile?.skills || [],
            values: valuesContext,
          }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429 && errData.retry_after_seconds) {
          const sec = errData.retry_after_seconds;
          const timeMsg = sec >= 60 ? `${Math.ceil(sec / 60)} minute(s)` : `${sec} seconds`;
          throw new Error(`Too many requests. Please try again in ${timeMsg}.`);
        }
        throw new Error(errData.error || 'Failed to get suggestions');
      }

      const data = await response.json();
      // career-coach returns { data: GoalSuggestions, updatedAt: ... }
      setSuggestions(data.data || data);
    } catch (err) {
      reportError(err, 'goals', { action: 'goal-suggestions-onboarding' });
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async (title: string, description: string, eType: string) => {
    const normalizedEType = eType.toLowerCase() as EType;
    const validETypes: EType[] = ['education', 'exposure', 'experience'];
    const selectedEType = validETypes.includes(normalizedEType) ? normalizedEType : 'education';
    const result = await onAddGoal(title, description, [selectedEType]);
    if (result) {
      setAddedGoalMap(prev => new Map(prev).set(title, result.id));
      toast({ title: 'Goal added!', description: `"${title}" has been added to your goals.` });
    }
  };

  const handleRemoveGoal = async (title: string) => {
    const goalId = addedGoalMap.get(title);
    if (!goalId || !onDeleteGoal) return;
    await onDeleteGoal(goalId);
    setAddedGoalMap(prev => { const next = new Map(prev); next.delete(title); return next; });
  };

  // Loading state with descriptive text
  if (loading) {
    return (
      <div className="py-8 space-y-6 max-w-lg mx-auto">
        <div className="text-center space-y-2">
          <div className="inline-flex p-3 rounded-full bg-gradient-to-br from-primary/20 to-accent/20">
            <Sparkles className="w-7 h-7 text-primary animate-pulse" />
          </div>
          <h2 className="text-xl font-display font-bold">Building Your Goal Suggestions</h2>
          <p className="text-sm text-muted-foreground">
            Analyzing your values and career profile...
          </p>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Error state — fallback to manual goal creation
  if (error || !suggestions) {
    return (
      <div className="text-center py-16 space-y-6">
        <div className="inline-flex p-4 rounded-full bg-muted">
          <AlertCircle className="w-8 h-8 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-display font-bold">Couldn't load suggestions</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {error || 'Something went wrong. You can create goals manually or try again.'}
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <CreateGoalDialog onCreateGoal={onAddGoal} />
          <Button variant="outline" onClick={() => { fetchedRef.current = false; fetchSuggestions(); }}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const goalsAdded = addedGoalMap.size;

  return (
    <div className="py-8 space-y-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex p-3 rounded-full bg-gradient-to-br from-primary/20 to-accent/20">
          <Sparkles className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-xl font-display font-bold">Suggested Goals Based on Your Values</h2>
        <p className="text-sm text-muted-foreground">
          Add goals that resonate with you, or create your own.
        </p>
      </div>

      {/* Goal Suggestions */}
      <GoalSuggestionsResults
        suggestions={suggestions}
        onAddGoal={handleAddGoal}
        onRemoveGoal={handleRemoveGoal}
        addedGoalTitles={new Set(addedGoalMap.keys())}
        getETypeColor={getETypeColor}
        getPriorityColor={getPriorityColor}
      />

      {/* CTAs */}
      <div className="flex flex-col items-center gap-3 pt-2">
        {(goalsAdded > 0 || (existingGoalCount ?? 0) > 0) && (
          <Button onClick={() => onDone?.()} className="gap-2">
            Review My Goals
            <ArrowRight className="w-4 h-4" />
          </Button>
        )}
        <p className="text-sm text-muted-foreground">
          or{' '}
          <CreateGoalDialog triggerVariant="link" onCreateGoal={async (...args) => {
            const result = await onAddGoal(...args);
            if (result) {
              toast({ title: 'Goal created!', description: `"${args[0]}" has been added to your goals.` });
            }
            return result;
          }} />
        </p>
        {(goalsAdded > 0 || (existingGoalCount ?? 0) > 0) && (
          <button
            onClick={() => onDone?.()}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip suggestions
          </button>
        )}
      </div>
    </div>
  );
};
